import { useMemo } from "react";
import useQuery from "hooks/useQuery";
import OneRepMaxService from "services/OneRepMaxService";
import type { OneRepMax } from "types/onerepmax";
import type { UseQueryOptions } from "types/use-query";

const EMPTY_FALLBACK_DATA: OneRepMax[] = [];

const cacheKeyPrefix = "OneRepMaxService.getByAthleteIds";

/** Matches every cached 1RM list fetched by athlete ids, whatever the exercise or athletes. */
export const getOneRepMaxesByAthleteIdsCacheKeyMatcher = () => (key: unknown) =>
  Array.isArray(key) && key[0] === cacheKeyPrefix;

/**
 * Fetches the 1RMs of several athletes for one exercise.
 */
function useGetOneRepMaxesByAthleteIdsQuery(
  exerciseId: string | null,
  athleteIds: string[],
  params?: { mostRecent?: boolean },
  options?: UseQueryOptions
) {
  const { enabled = true, ...swrOptions } = options ?? {};
  const mostRecent = params?.mostRecent;
  const key = useMemo(() => {
    if (!exerciseId || !athleteIds.length || !enabled) return null;
    return [cacheKeyPrefix, exerciseId, athleteIds, mostRecent] as const;
  }, [exerciseId, athleteIds, enabled, mostRecent]);

  return useQuery(
    key,
    ([, eid, aids, recent]) => {
      const requestParams = recent !== undefined ? { mostRecent: recent } : undefined;
      return OneRepMaxService.getByAthleteIds(eid, aids, requestParams);
    },
    { fallbackData: EMPTY_FALLBACK_DATA, ...swrOptions }
  );
}

export default useGetOneRepMaxesByAthleteIdsQuery;
